/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type MouseEvent, type ReactNode } from 'react';

import { jsx } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { SearchLinkedResultCategory } from '@atlassian/search-client/common/types';

import {
	LinkDocContentType,
	type ResultClickedCallback,
	type ResultClickedCallbackParams,
} from '../../../constants/types';
import { isAuxClick } from '../../../utils/clicks';
import type { AdvancedSearchResultInteractedMetadata } from '../../../utils/events/advanced-search-result-interacted';
import { Link } from '../../link-component';
import { addVisitedLink, getVisitedLinks, isVisited } from '../../search-result-title/utils';

const styles = cssMap({
	headingArea: {
		gridArea: 'heading',
		font: token('font.body'),
		minWidth: '476px',
	},
	title: {
		display: 'inline-flex',
		overflow: 'hidden',
		paddingLeft: token('space.050'),
	},
	titleText: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	visitedLink: {
		'&:link': {
			textDecoration: 'none',
			borderBottom: 'none',
		},
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:visited': {
			color: token('color.link.visited'),
		},
		'&:visited:active': {
			color: token('color.link.visited.pressed'),
		},
	},
	visitedLinkPatch: {
		color: token('color.link.visited'),
		'&:active': {
			color: token('color.link.visited.pressed'),
		},
	},
	grid: {
		gridTemplateColumns: 'minmax(50%, 1fr) min-content',
		gap: token('space.400'),
	},
});

export type FireResultClickedProps = {
	metadata: AdvancedSearchResultInteractedMetadata;
	position?: number;
	socialSignal?: string;
};

type LinkedResultTitleProps = {
	sublistPosition: number;
	id?: string;
	title: ReactNode;
	url?: string;
	onResultClicked?: ResultClickedCallback;
	onResultContextMenu?: ResultClickedCallback;
	provider?: string;
	category: SearchLinkedResultCategory | undefined;
};

export const LinkedResultTitle: (props: LinkedResultTitleProps) => JSX.Element = (
	props: LinkedResultTitleProps,
) => {
	const {
		id,
		title,
		url,
		onResultClicked,
		onResultContextMenu,
		sublistPosition,
		provider,
		category,
	} = props;

	/**
	 * A dynamic search_id query param and queryHash is appended to the document URL for 1P search results.
	 * As result we are unable to use the browser's visited links history to determine if a link has been visited.
	 * This is workaround until we have a better solution.
	 */
	const visitedLinks = getVisitedLinks();
	const isVisitedLink = id ? isVisited(visitedLinks, id) : false;

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			xcss={styles.headingArea}
			onClick={() => {
				if (id) {
					onResultClicked?.({
						fromLinksSeeAll: false,
						sublistPosition: sublistPosition,
						provider,
						fanoutProduct: provider,
						linkDocId: id,
						linkDocContentType: LinkDocContentType.boostedLink,
						fanoutLinkCategory: category,
					} satisfies ResultClickedCallbackParams);
					addVisitedLink(id);
				}
			}}
			onContextMenu={() => {
				if (id) {
					onResultContextMenu?.({
						sublistPosition: sublistPosition,
						fanoutProduct: provider,
						linkDocId: id,
						linkDocContentType: LinkDocContentType.boostedLink,
						fanoutLinkCategory: category,
					} satisfies ResultClickedCallbackParams);
				}
			}}
			onAuxClick={(e: MouseEvent<HTMLDivElement>) => {
				if (isAuxClick(e) && id && fg('add-middle-click-event-fanout-results')) {
					onResultClicked?.({
						fromLinksSeeAll: false,
						sublistPosition: sublistPosition,
						provider,
						fanoutProduct: provider,
						linkDocId: id,
						linkDocContentType: LinkDocContentType.boostedLink,
						fanoutLinkCategory: category,
						isAuxClick: true,
						tmpAuxDiagnostics: {
							button: e.button,
							nativeButton: e.nativeEvent?.button,
							eventType: e.type,
							isTrusted: e.nativeEvent?.isTrusted,
							metaKey: e.metaKey,
							ctrlKey: e.ctrlKey,
						},
					} satisfies ResultClickedCallbackParams);
				}
			}}
		>
			{id && url ? (
				<Grid xcss={styles.grid}>
					<Flex alignItems="center">
						<Link
							xcss={cx(styles.visitedLink, isVisitedLink && styles.visitedLinkPatch, styles.title)}
							href={url}
							testId="search-page-linked-result-link"
						>
							<Box xcss={styles.titleText}>{title}</Box>
						</Link>
					</Flex>
				</Grid>
			) : (
				<Box xcss={styles.title}>
					<Box xcss={styles.titleText}>{title}</Box>
				</Box>
			)}
		</Box>
	);
};
